import type { Id, SymptomType } from '../domain/types'

/** Niveau d'agregation auquel une association est calculee. */
export type NiveauAnalyse =
  | 'ingredient'
  | 'recette'
  | 'famille'
  | 'categorie'
  | 'charge'
  | 'facteur'

export const NIVEAU_LABELS: Record<NiveauAnalyse, string> = {
  ingredient: 'Ingrédient',
  recette: 'Recette',
  famille: 'Famille FODMAP',
  categorie: 'Catégorie',
  charge: 'Charge cumulée',
  facteur: 'Facteur de vie',
}

// ---------------------------------------------------------------------------
// Fenetres d'imputation
// ---------------------------------------------------------------------------

/**
 * Intervalle, apres un repas, dans lequel on cherche le debut d'un episode.
 *
 * Une fenetre n'est pas un reglage cosmetique : c'est **une hypothese
 * mecanistique**. Chercher entre 6 et 24 h revient a postuler une fermentation
 * colique, et rend du meme coup indetectable tout ce qui releve d'un autre
 * mecanisme. C'est pourquoi le moteur en teste plusieurs.
 */
export interface FenetreAnalyse {
  cle: string
  /** Libelle court, affiche comme puce sur une association. */
  libelle: string
  /** Debut de la fenetre, en heures apres le repas. */
  debutH: number
  /** Fin de la fenetre, en heures apres le repas. */
  finH: number
  /** Mecanisme physiologique que cette fenetre isole. */
  mecanisme: string
}

/**
 * Les quatre horloges de la digestion.
 *
 * Elles se recouvrent volontairement : a 4 h, un ballonnement peut venir aussi
 * bien d'un afflux d'eau dans le grele que du debut de la fermentation cæcale.
 * Aucune fenetre ne peut donc trancher seule — mais **la fenetre dans laquelle
 * un signal apparait nomme le mecanisme**, et c'est la l'information utile.
 *
 * Reperes physiologiques :
 *  - transit oro-cæcal : 72 a 90 min chez le sujet sain, d'ou le debut du grele
 *    a 2 h et de la fermentation a 6 h ;
 *  - transit total : 12 a 48 h, jusqu'a 72 h. C'est l'echelle propre de la
 *    **forme des selles** : un Bristol a 1 ou a 7 reflete des jours de transit,
 *    pas un repas. La fenetre 6-24 h y est mecaniquement inadaptee.
 */
export const FENETRES_DEFAUT: readonly FenetreAnalyse[] = [
  {
    cle: 'postprandial',
    libelle: '0–3 h',
    debutH: 0,
    finH: 3,
    mecanisme: 'Réflexe gastro-colique, distension',
  },
  {
    cle: 'grele',
    libelle: '2–6 h',
    debutH: 2,
    finH: 6,
    mecanisme: 'Charge osmotique du grêle',
  },
  {
    cle: 'colique',
    libelle: '6–24 h',
    debutH: 6,
    finH: 24,
    mecanisme: 'Fermentation colique',
  },
  {
    cle: 'differe',
    libelle: '24–48 h',
    debutH: 24,
    finH: 48,
    mecanisme: 'Dose cumulée, transit total',
  },
] as const

export interface OptionsAnalyse {
  /**
   * Fenetres d'imputation testees, toutes dans la **meme famille** de tests.
   *
   * Les correger separement reviendrait a constituer autant de familles
   * independantes controlees chacune a 5 % — exactement l'erreur decrite plus
   * bas a propos du decoupage par symptome.
   */
  fenetres: readonly FenetreAnalyse[]
  /** Nombre minimal de repas exposes pour qu'un test soit lance. */
  minRepasExposes: number
  /** Nombre minimal de declenchements observes chez les exposes. */
  minEvenements: number
  /** Nombre minimal d'episodes **distincts** pour parler de piste serieuse. */
  minEpisodesDistincts: number
  /**
   * Seuil de q-value (FDR) pour retenir une piste serieuse.
   *
   * Fixe a 0,05 et non 0,10 : une fausse piste conduit a supprimer un aliment
   * pendant des semaines pour rien. Le cout d'un faux positif est donc bien
   * superieur a celui d'un faux negatif, qui ne coute qu'un delai — et le
   * palier « a surveiller » rattrape de toute facon les signaux plus faibles.
   */
  seuilQ: number
  /**
   * p-value brute en deca de laquelle on lance le test de permutation (couteux).
   *
   * Le seuil effectif est `max(seuilPrefiltre, seuilQ)` : Benjamini-Hochberg
   * garantit q >= p, donc une ligne dont le p de Fisher depasse `seuilQ` ne peut
   * jamais atteindre `qFisher <= seuilQ` et ne peut donc jamais devenir une
   * piste serieuse. La permuter serait du calcul pur perdu. Le `max` protege le
   * cas ou l'on releverait `seuilQ` au-dessus de ce seuil, qui priverait alors
   * de permutation des lignes encore eligibles au verdict.
   *
   * Baisser ce seuil ne change **aucune** q-value : les lignes non permutees
   * entrent dans la famille BH des permutations avec p = 1 et s'y classent en
   * dernier, ce qui laisse intacts les rangs — donc les q — des lignes calculees.
   */
  seuilPrefiltre: number
  /** Nombre de decalages circulaires du test de permutation. */
  permutations: number
  /** Graine du generateur pseudo-aleatoire (resultats reproductibles). */
  graine: number
  /** Similarite de Jaccard au-dela de laquelle deux expositions sont jugees indissociables. */
  seuilJaccard: number
}

export const OPTIONS_DEFAUT: OptionsAnalyse = {
  fenetres: FENETRES_DEFAUT,
  minRepasExposes: 5,
  minEvenements: 3,
  minEpisodesDistincts: 3,
  seuilQ: 0.05,
  seuilPrefiltre: 0.05,
  permutations: 2000,
  graine: 20260730,
  seuilJaccard: 0.8,
}

export type Verdict =
  | 'piste-serieuse'
  | 'a-surveiller'
  | 'probablement-non-lie'
  | 'donnees-insuffisantes'

export const VERDICT_LABELS: Record<Verdict, string> = {
  'piste-serieuse': 'Piste sérieuse',
  'a-surveiller': 'À surveiller',
  'probablement-non-lie': 'Probablement non lié',
  'donnees-insuffisantes': 'Données insuffisantes',
}

export interface Contingence {
  /** Exposes avec declenchement dans la fenetre. */
  a: number
  /** Exposes sans declenchement. */
  b: number
  /** Non exposes avec declenchement. */
  c: number
  /** Non exposes sans declenchement. */
  d: number
}

export interface Stratification {
  /** Libelle de la strate, ex. « stress eleve ». */
  libelle: string
  contingence: Contingence
  risqueRelatif: number
  /** `false` si la strate n'atteint pas les seuils minimaux. */
  exploitable: boolean
}

export interface ResultatDose {
  /** p-value du test U de Mann-Whitney sur la charge. */
  p: number
  /** AUC : probabilite qu'un repas suivi d'un symptome porte une charge superieure. */
  auc: number
  /** Taux de declenchement par tertile de charge (faible, moyenne, elevee). */
  tauxParTertile: [number, number, number]
  effectifsParTertile: [number, number, number]
  /** `true` si le taux croit de facon monotone avec la charge. */
  monotone: boolean
}

/** Ce qu'une fenetre donne pour un couple, dans le profil de latence. */
export interface MesureFenetre {
  fenetre: FenetreAnalyse
  contingence: Contingence
  repasExposes: number
  repasExploitables: number
  episodesDistincts: number
  risqueExpose: number
  risqueNonExpose: number
  risqueRelatif: number
  icRisqueExpose: { bas: number; haut: number }
  /** p de Fisher **non corrige**, pour cette fenetre seule. Indicatif. */
  pFisher: number
  /** `false` si les effectifs minimaux ne sont pas atteints dans cette fenetre. */
  exploitable: boolean
  /** `true` si la fenetre entre dans la statistique du maximum. */
  retenue: boolean
}

export interface Association {
  cle: string
  libelle: string
  niveau: NiveauAnalyse
  symptome: SymptomType
  /**
   * Fenetre retenue : celle qui porte le plus fort ecart de risque.
   *
   * C'est l'argmax de la statistique du maximum, et donc **le mecanisme
   * designe** par les donnees — un signal a 0-3 h ne dit pas la meme chose
   * qu'un signal a 24-72 h.
   */
  fenetre: FenetreAnalyse

  /**
   * Profil de latence : ce que donne *chaque* fenetre pour ce couple.
   *
   * Descriptif, jamais decisionnel — le verdict porte sur le couple entier, pas
   * sur une fenetre prise isolement. Sert a montrer *quand* le signal apparait,
   * et a distinguer un pic net d'un effet etale sur toutes les fenetres.
   */
  profil: MesureFenetre[]

  contingence: Contingence
  /** Repas exploitables et exposes (= a + b). */
  repasExposes: number
  /** Repas exploitables au total. */
  repasExploitables: number
  /** Nombre d'episodes **distincts** imputes a cette exposition. */
  episodesDistincts: number

  risqueExpose: number
  risqueNonExpose: number
  risqueRelatif: number
  icRisqueExpose: { bas: number; haut: number }

  /** p-value du test exact de Fisher. */
  pFisher: number
  /** q-value (Benjamini-Hochberg) sur la famille des tests exploitables. */
  qFisher: number
  /**
   * p-value du test de permutation par decalage circulaire.
   * `null` si le pre-filtre ne l'a pas declenchee (test couteux).
   */
  pPermutation: number | null
  /**
   * q-value (Benjamini-Hochberg) des p-values de permutation.
   *
   * C'est le critere de decision principal. La p-value de permutation brute ne
   * peut pas etre comparee a 0,05 telle quelle : elle n'est calculee que sur des
   * candidats **deja selectionnes** pour leur p de Fisher, et sur une centaine
   * de couples testes, un minimum a 0,003 est parfaitement banal.
   */
  qPermutation: number | null

  verdict: Verdict
  /** Raison lisible quand le verdict est « données insuffisantes ». */
  motifInsuffisance?: string

  /** Cles jugees indissociables de celle-ci (co-occurrence quasi totale). */
  indissociablesDe: string[]

  /** Stratification par niveau de stress, si les donnees le permettent. */
  stratesStress?: Stratification[]

  /** Analyse d'effet-dose, seulement au niveau « famille ». */
  dose?: ResultatDose
}

export interface QualiteDonnees {
  nbRepas: number
  nbEpisodes: number
  nbJoursSuivis: number
  nbJoursAvecRepas: number
  nbJoursAvecJournal: number
  /** Repas exclus car leur fenetre depasse l'instant present. */
  repasTropRecents: number
  /** Score 0-100, heuristique de fiabilite globale des analyses. */
  score: number
  /** Messages explicatifs a afficher a l'utilisateur. */
  avertissements: string[]
}

export interface ResultatAnalyse {
  genereLe: number
  options: OptionsAnalyse
  qualite: QualiteDonnees
  associations: Association[]
  /** Groupes d'expositions indissociables (co-occurrence >= seuilJaccard). */
  blocsIndissociables: { cles: string[]; libelles: string[] }[]
}

/** Exposition candidate : une cle, un libelle, et la dose portee par chaque repas. */
export interface Exposition {
  cle: string
  libelle: string
  niveau: NiveauAnalyse
  /** Index des repas (dans le tableau trie) ou l'exposition est presente. */
  repasIdx: number[]
  /** Charge associee, meme indexation que `repasIdx`. Sert a l'effet-dose. */
  charges: number[]
}

export interface RepasAnalyse {
  id: Id
  t: number
  ingredientIds: Set<Id>
}
